/**
 * Marketplace listing helpers (SYN-1421, Command UI).
 *
 * Listings are discovery/catalog records. They are intentionally rendered
 * separately from readiness snapshots so discovery never becomes a readiness
 * verdict at the UI boundary.
 */

export type ContractListingState = "visible" | "blocked" | "unavailable";

export type ContractListingRequirementStatus = "satisfied" | "blocked" | "unknown";

export interface ContractListingContractSummary {
  id: string;
  name: string;
  description?: string;
  sector?: string;
  function?: string;
  capability?: string;
  bundle?: string;
  riskClass?: string;
  trustDefault?: string;
  regulations: string[];
  requires: string[];
  inputSchema?: string;
  outputSchema?: string;
}

export interface ContractListingRequirement {
  key: string;
  type: string;
  label: string;
  required: boolean;
  status: ContractListingRequirementStatus;
  sourceField?: string;
  sourceRef?: string;
  connectionId?: string;
  connectionResolution?: "resolved" | "unresolved" | "ambiguous";
  metadata?: Record<string, unknown>;
}

export interface ContractListing {
  id: string;
  listingKey: string;
  title?: string;
  summary?: string;
  listingState: ContractListingState;
  stateReasons: string[];
  contract: ContractListingContractSummary;
  requirements: ContractListingRequirement[];
  metadata: Record<string, unknown>;
}

type Wire = Record<string, unknown>;

const LISTING_STATE_SET = new Set<ContractListingState>(["visible", "blocked", "unavailable"]);

export const LISTING_STATE_META: Record<
  ContractListingState,
  { label: string; badgeClass: string; dotClass: string }
> = {
  visible: {
    label: "Listed",
    badgeClass: "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
    dotClass: "bg-emerald-500",
  },
  blocked: {
    label: "Blocked",
    badgeClass: "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300",
    dotClass: "bg-amber-500",
  },
  unavailable: {
    label: "Unavailable",
    badgeClass: "border-muted bg-muted/40 text-muted-foreground",
    dotClass: "bg-muted-foreground",
  },
};

function isRecord(value: unknown): value is Wire {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function pick(record: Wire, snake: string, camel: string): unknown {
  return record[camel] !== undefined ? record[camel] : record[snake];
}

function str(record: Wire, snake: string, camel: string): string | undefined {
  const value = pick(record, snake, camel);
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

function bool(record: Wire, snake: string, camel: string): boolean | undefined {
  const value = pick(record, snake, camel);
  return typeof value === "boolean" ? value : undefined;
}

function record(value: unknown): Record<string, unknown> | undefined {
  return isRecord(value) ? value : undefined;
}

function strArray(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((v): v is string => typeof v === "string" && v.length > 0)
    : [];
}

function listingState(value: unknown): ContractListingState {
  return typeof value === "string" && LISTING_STATE_SET.has(value as ContractListingState)
    ? (value as ContractListingState)
    : "unavailable";
}

function contract(raw: unknown): ContractListingContractSummary | undefined {
  const r = record(raw);
  if (!r) {
    return undefined;
  }
  const id = str(r, "id", "id");
  const name = str(r, "name", "name");
  if (!id || !name) {
    return undefined;
  }
  return {
    id,
    name,
    description: str(r, "description", "description"),
    sector: str(r, "sector", "sector"),
    function: str(r, "function", "function"),
    capability: str(r, "capability", "capability"),
    bundle: str(r, "bundle", "bundle"),
    riskClass: str(r, "risk_class", "riskClass"),
    trustDefault: str(r, "trust_default", "trustDefault"),
    regulations: strArray(pick(r, "regulations", "regulations")),
    requires: strArray(pick(r, "requires", "requires")),
    inputSchema: str(r, "input_schema", "inputSchema"),
    outputSchema: str(r, "output_schema", "outputSchema"),
  };
}

function requirement(raw: unknown): ContractListingRequirement | undefined {
  const r = record(raw);
  if (!r) {
    return undefined;
  }
  const key = str(r, "key", "key");
  const type = str(r, "type", "type");
  const label = str(r, "label", "label");
  if (!key || !type || !label) {
    return undefined;
  }
  const status = str(r, "status", "status");
  return {
    key,
    type,
    label,
    required: bool(r, "required", "required") ?? false,
    status:
      status === "satisfied" || status === "blocked" || status === "unknown" ? status : "unknown",
    sourceField: str(r, "source_field", "sourceField"),
    sourceRef: str(r, "source_ref", "sourceRef"),
    connectionId: str(r, "connection_id", "connectionId"),
    connectionResolution:
      pick(r, "connection_resolution", "connectionResolution") === "resolved" ||
      pick(r, "connection_resolution", "connectionResolution") === "unresolved" ||
      pick(r, "connection_resolution", "connectionResolution") === "ambiguous"
        ? (pick(r, "connection_resolution", "connectionResolution") as
            | "resolved"
            | "unresolved"
            | "ambiguous")
        : undefined,
    metadata: record(pick(r, "metadata", "metadata")),
  };
}

export function normalizeListing(raw: Record<string, unknown>): ContractListing | undefined {
  const id = str(raw, "id", "id");
  const listingKey = str(raw, "listing_key", "listingKey");
  const summary = str(raw, "summary", "summary");
  const parsedContract = contract(pick(raw, "contract", "contract"));
  if (!id || !listingKey || !parsedContract) {
    return undefined;
  }
  const requirementsRaw = pick(raw, "requirements", "requirements");
  return {
    id,
    listingKey,
    title: str(raw, "title", "title"),
    summary,
    listingState: listingState(pick(raw, "listing_state", "listingState")),
    stateReasons: strArray(pick(raw, "state_reasons", "stateReasons")),
    contract: parsedContract,
    requirements: Array.isArray(requirementsRaw)
      ? requirementsRaw
          .map(requirement)
          .filter((req): req is ContractListingRequirement => req !== undefined)
      : [],
    metadata: record(pick(raw, "metadata", "metadata")) ?? {},
  };
}

function metadataCard(listing: ContractListing): Record<string, unknown> | undefined {
  const card = listing.metadata.card;
  return record(card);
}

export function listingTitle(listing: ContractListing): string {
  const cardTitle = metadataCard(listing)?.title;
  return typeof cardTitle === "string" && cardTitle.length > 0
    ? cardTitle
    : (listing.title ?? listing.contract.name);
}

export function listingDescription(listing: ContractListing): string | undefined {
  const cardDescription = metadataCard(listing)?.description;
  return typeof cardDescription === "string" && cardDescription.length > 0
    ? cardDescription
    : (listing.summary ?? listing.contract.description);
}

export function listingCategory(listing: ContractListing): string | undefined {
  const category = listing.metadata.category;
  return typeof category === "string" && category.length > 0
    ? category
    : (listing.contract.sector ?? listing.contract.function);
}

export function listingSearchText(listing: ContractListing): string {
  return [
    listingTitle(listing),
    listingDescription(listing) ?? "",
    listingCategory(listing) ?? "",
    listing.contract.sector ?? "",
    listing.contract.function ?? "",
    listing.contract.capability ?? "",
    listing.listingKey,
    ...listing.requirements.map((req) => req.label),
  ]
    .join(" ")
    .toLowerCase();
}
